// src/pages/TestimonialsPage.js

import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import { ThemeContext } from '../context/ThemeContext';
import { testimonialService } from '../api';
import SectionTitle from '../components/common/SectionTitle';
import PageLoader from '../components/common/PageLoader';
import ErrorDisplay from '../components/common/ErrorDisplay';

// Styled components
const TestimonialsSection = styled.section`
  padding: 5rem 2rem;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TestimonialCard = styled(motion.div)`
  background: ${props => props.theme.card.background};
  border-radius: 10px;
  box-shadow: ${props => props.theme.card.shadow};
  padding: 2rem;
  position: relative;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${props => props.theme.card.hover};
  }
  
  &:before {
    content: '"';
    position: absolute;
    top: 10px;
    left: 20px;
    font-size: 5rem;
    font-family: Georgia, serif;
    color: ${props => props.theme.primary}20;
    line-height: 1;
  }
`;

const TestimonialContent = styled.div`
  position: relative;
  z-index: 1;
`;

const TestimonialText = styled.p`
  color: ${props => props.theme.text};
  font-style: italic;
  line-height: 1.7;
  margin-bottom: 1.5rem;
  font-size: 1.1rem;
`;

const ClientInfo = styled.div`
  display: flex;
  align-items: center;
`;

const ClientImage = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-image: url(${props => props.src || '/default-avatar.jpg'});
  background-size: cover;
  background-position: center;
  border: 2px solid ${props => props.theme.primary};
  margin-right: 1rem;
  flex-shrink: 0;
`;

const ClientDetails = styled.div`
  flex: 1;
`;

const ClientName = styled.h4`
  color: ${props => props.theme.text};
  margin-bottom: 0.2rem;
  font-size: 1.1rem;
`;

const ClientRole = styled.p`
  color: ${props => props.theme.primary};
  font-size: 0.9rem;
`;

const RatingStars = styled.div`
  display: flex;
  margin-top: 0.5rem;
  color: ${props => props.theme.accent};
  font-size: 1rem;
`;

const FeaturedTestimonial = styled.div`
  margin-bottom: 4rem;
`;

const FeaturedCard = styled(motion.div)`
  background: ${props => props.theme.card.background};
  border-radius: 10px;
  box-shadow: ${props => props.theme.card.shadow};
  padding: 3rem;
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 2rem;
  position: relative;
  
  &:before {
    content: '"';
    position: absolute;
    top: 20px;
    left: 30px;
    font-size: 8rem;
    font-family: Georgia, serif;
    color: ${props => props.theme.primary}20;
    line-height: 1;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 2rem;
  }
`;

const FeaturedClientInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const FeaturedClientImage = styled.div`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background-image: url(${props => props.src || '/default-avatar.jpg'});
  background-size: cover;
  background-position: center;
  border: 3px solid ${props => props.theme.primary};
  margin-bottom: 1.5rem;
`;

const FeaturedTestimonialContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const FeaturedTestimonialText = styled.p`
  color: ${props => props.theme.text};
  font-style: italic;
  line-height: 1.8;
  font-size: 1.3rem;
  margin-bottom: 2rem;
  position: relative;
`;

const NoResults = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${props => props.theme.text};
  opacity: 0.7;
  font-size: 1.2rem;
`;

const AddYourTestimonial = styled.div`
  margin-top: 4rem;
  text-align: center;
  background: ${props => props.theme.backgroundSecondary};
  padding: 3rem;
  border-radius: 10px;
`;

const AddYourTestimonialTitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: ${props => props.theme.text};
`;

const AddYourTestimonialText = styled.p`
  color: ${props => props.theme.text};
  max-width: 700px;
  margin: 0 auto 2rem;
  opacity: 0.8;
`;

const TestimonialButton = styled.a`
  display: inline-block;
  padding: 0.8rem 1.5rem;
  background-color: ${props => props.primary ? props.theme.primary : 'transparent'};
  color: ${props => props.primary ? 'white' : props.theme.primary};
  text-decoration: none;
  border-radius: 4px;
  font-weight: 500;
  transition: all 0.2s ease;
  border: 1px solid ${props => props.theme.primary};
  
  &:hover {
    background-color: ${props => props.primary ? props.theme.primaryDark || props.theme.primary + 'dd' : props.theme.primary + '10'};
    transform: translateY(-2px);
    box-shadow: ${props => props.primary ? '0 4px 8px rgba(0, 0, 0, 0.1)' : 'none'};
  }
`;

const NavigationButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: transparent;
  color: ${props => props.theme.primary};
  border: 1px solid ${props => props.theme.primary};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.primary}20;
  }
`;

const TestimonialsPage = () => {
  const { theme } = useContext(ThemeContext);
  const { t } = useTranslation();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // État pour le témoignage en vedette actuel (s'il y en a plusieurs)
  const [currentFeaturedIndex, setCurrentFeaturedIndex] = useState(0);
  
  // Charger les témoignages depuis l'API
  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        setLoading(true);
        
        // Appel à l'API pour récupérer les témoignages
        const response = await testimonialService.getAll();
        
        // Vérifier la structure de la réponse
        let testimonialsData = [];
        if (response.data && Array.isArray(response.data.results)) {
          testimonialsData = response.data.results;
        } else if (Array.isArray(response.data)) {
          testimonialsData = response.data;
        }
        
        console.log("Testimonials data:", testimonialsData);
        setTestimonials(testimonialsData);
        
        // Mise à jour du titre de la page
        document.title = `${t('clientTestimonials')} | PropentaTech`;
      } catch (err) {
        console.error("Error fetching testimonials:", err);
        setError(err.response?.data?.detail || t('errorLoadingTestimonials'));
      } finally {
        setLoading(false);
      }
    };
    
    fetchTestimonials();
    
    return () => {
      document.title = 'PropentaTech';
    };
  }, [t]);
  
  // Extraire les témoignages mis en avant
  const featuredTestimonials = testimonials.filter(testimonial => 
    testimonial.is_featured
  );
    
  // Extraire les témoignages réguliers
  const regularTestimonials = testimonials.filter(testimonial => 
    !testimonial.is_featured
  );
  
  // Fonction pour passer au témoignage en vedette suivant
  const nextFeatured = () => {
    setCurrentFeaturedIndex((prevIndex) => 
      prevIndex === featuredTestimonials.length - 1 ? 0 : prevIndex + 1
    );
  };
  
  // Fonction pour passer au témoignage en vedette précédent
  const prevFeatured = () => {
    setCurrentFeaturedIndex((prevIndex) => 
      prevIndex === 0 ? featuredTestimonials.length - 1 : prevIndex - 1
    );
  };
  
  // Afficher des étoiles pour la notation
  const renderStars = (rating) => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <i key={i} className={i < rating ? 'fas fa-star' : 'far fa-star'}></i>
      );
    }
    return stars;
  };
  
  if (loading) {
    return <PageLoader />;
  }
  
  if (error) {
    return <ErrorDisplay error={error} />;
  }
  
  return (
    <TestimonialsSection>
      <Container>
        <SectionTitle
          title={t('clientTestimonials')}
          subtitle={t('clientTestimonialsSubtitle')}
        />
        
        {testimonials.length === 0 ? (
          <NoResults theme={theme}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>
                <i className="fas fa-comments"></i>
              </div>
              <h3>{t('noTestimonials')}</h3>
              <p>{t('beTheFirstToShare')}</p>
            </motion.div>
          </NoResults>
        ) : (
          <>
            {featuredTestimonials.length > 0 && (
              <FeaturedTestimonial>
                <AnimatePresence mode="wait">
                  <FeaturedCard
                    key={featuredTestimonials[currentFeaturedIndex].id}
                    theme={theme}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.5 }}
                  >
                    <FeaturedClientInfo>
                      <FeaturedClientImage 
                        src={featuredTestimonials[currentFeaturedIndex].image}
                        theme={theme}
                      />
                      <ClientName theme={theme}>
                        {featuredTestimonials[currentFeaturedIndex].name}
                      </ClientName>
                      <ClientRole theme={theme}>
                        {featuredTestimonials[currentFeaturedIndex].position}
                        {featuredTestimonials[currentFeaturedIndex].company && (
                          ` @ ${featuredTestimonials[currentFeaturedIndex].company}`
                        )}
                      </ClientRole>
                      <RatingStars theme={theme}>
                        {renderStars(featuredTestimonials[currentFeaturedIndex].rating)}
                      </RatingStars>
                      
                      {featuredTestimonials.length > 1 && (
                        <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem' }}>
                          <NavigationButton
                            onClick={prevFeatured}
                            aria-label={t('previousTestimonial')}
                            theme={theme}
                          >
                            <i className="fas fa-chevron-left"></i>
                          </NavigationButton>
                          <NavigationButton
                            onClick={nextFeatured}
                            aria-label={t('nextTestimonial')}
                            theme={theme}
                          >
                            <i className="fas fa-chevron-right"></i>
                          </NavigationButton>
                        </div>
                      )}
                    </FeaturedClientInfo>
                    
                    <FeaturedTestimonialContent>
                      <FeaturedTestimonialText theme={theme}>
                        {featuredTestimonials[currentFeaturedIndex].text || 
                         featuredTestimonials[currentFeaturedIndex].testimonial}
                      </FeaturedTestimonialText>
                    </FeaturedTestimonialContent>
                  </FeaturedCard>
                </AnimatePresence>
              </FeaturedTestimonial>
            )}
            
            {regularTestimonials.length > 0 && (
              <TestimonialsGrid>
                {regularTestimonials.map((testimonial, index) => (
                  <TestimonialCard
                    key={testimonial.id}
                    theme={theme}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <TestimonialContent>
                      <TestimonialText theme={theme}>
                        {testimonial.text || testimonial.testimonial}
                      </TestimonialText>
                      <ClientInfo>
                        {testimonial.image && (
                          <ClientImage src={testimonial.image} theme={theme} />
                        )}
                        <ClientDetails>
                          <ClientName theme={theme}>{testimonial.name}</ClientName>
                          <ClientRole theme={theme}>
                            {testimonial.position}
                            {testimonial.company && ` @ ${testimonial.company}`}
                          </ClientRole>
                          <RatingStars theme={theme}>
                            {renderStars(testimonial.rating || 5)}
                          </RatingStars>
                        </ClientDetails>
                      </ClientInfo>
                    </TestimonialContent>
                  </TestimonialCard>
                ))}
              </TestimonialsGrid>
            )}
            
            <AddYourTestimonial theme={theme}>
              <AddYourTestimonialTitle theme={theme}>
                {t('shareYourExperience')}
              </AddYourTestimonialTitle>
              <AddYourTestimonialText theme={theme}>
                {t('shareYourExperienceDesc')}
              </AddYourTestimonialText>
              <TestimonialButton
                href="/contact"
                theme={theme}
                primary
              >
                {t('shareTestimonial')}
              </TestimonialButton>
            </AddYourTestimonial>
          </>
        )}
      </Container>
    </TestimonialsSection>
  );
};

export default TestimonialsPage;